/**
 * FileUpload Component
 *
 * Drag-and-drop modal for uploading files to the user's current folder.
 * Empty destination means the server default Inbox.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useFileStore } from '@/stores/fileStore';
import { api, type WorkspaceAsset, type WorkspaceAssetUploadResult } from '@/services/api';
import { needsChunkedUpload, uploadFileInChunks } from '@/services/chunkedUpload';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Upload,
  X,
  File,
  FileText,
  FileSpreadsheet,
  FileImage,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FolderUp,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface FileUploadProps {
  /** Whether the upload modal/panel is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /**
   * Destination directory for the upload. Empty string means the server
   * default Inbox. Files-tab callers pass the user's current folder when one
   * is selected.
   */
  destination?: string;
  /** Fires after at least one file successfully uploads. Lets the caller refresh its own listing. */
  onUploaded?: () => void | Promise<void>;
}

interface QueuedFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  /** Percent complete, only tracked for chunked uploads. */
  progress?: number;
}

// ============================================
// Helpers
// ============================================

/**
 * Get an icon component for a file based on its type
 */
function getFileIcon(file: File) {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const type = file.type;

  // Images
  if (type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'bmp'].includes(ext)) {
    return FileImage;
  }

  // Spreadsheets / CSV
  if (['csv', 'tsv', 'xlsx', 'xls'].includes(ext) || type.includes('spreadsheet')) {
    return FileSpreadsheet;
  }

  // Text-based files
  if (type.startsWith('text/') || ['txt', 'md', 'json', 'xml', 'yaml', 'yml'].includes(ext)) {
    return FileText;
  }

  return File;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Generate a unique ID for queued files
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================
// FileUpload Component
// ============================================

export function FileUpload({ isOpen, onClose, destination = '', onUploaded }: FileUploadProps) {
  const { refreshTree } = useFileStore();

  // State
  const [isDragging, setIsDragging] = useState(false);
  const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<WorkspaceAssetUploadResult | null>(null);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const dragCounter = useRef(0);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQueuedFiles([]);
      setUploadResult(null);
      setIsDragging(false);
    }
  }, [isOpen]);

  // ----------------------------------------
  // Drag and Drop Handlers
  // ----------------------------------------

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // ----------------------------------------
  // Queue Management
  // ----------------------------------------

  const addFilesToQueue = useCallback((files: File[]) => {
    const newQueuedFiles: QueuedFile[] = files.map((file) => ({
      id: generateId(),
      file,
      status: 'pending',
    }));
    setQueuedFiles((prev) => [...prev, ...newQueuedFiles]);
    setUploadResult(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      addFilesToQueue(files);
    }
  }, [addFilesToQueue]);

  // ----------------------------------------
  // File Selection
  // ----------------------------------------

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) {
      addFilesToQueue(files);
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [addFilesToQueue]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const removeFromQueue = useCallback((id: string) => {
    setQueuedFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearQueue = useCallback(() => {
    setQueuedFiles([]);
    setUploadResult(null);
  }, []);

  // ----------------------------------------
  // Upload Handler
  // ----------------------------------------

  const handleUpload = useCallback(async () => {
    const pendingFiles = queuedFiles.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsUploading(true);

    // Mark all pending files as uploading
    setQueuedFiles((prev) =>
      prev.map((f) =>
        f.status === 'pending' ? { ...f, status: 'uploading' as const } : f
      )
    );

    try {
      // Files over the threshold cannot go through a single request because
      // Cloudflare rejects the body before it reaches the server, so they
      // upload one at a time as chunks. Smaller files keep the batch path.
      const largeFiles = pendingFiles.filter((qf) => needsChunkedUpload(qf.file));
      const smallFiles = pendingFiles.filter((qf) => !needsChunkedUpload(qf.file));

      const uploaded: WorkspaceAsset[] = [];
      const failed: { filename: string; error: string }[] = [];

      if (smallFiles.length > 0) {
        const result = await api.workspace.assets.upload(
          smallFiles.map((qf) => qf.file),
          destination
        );
        uploaded.push(...result.uploaded);
        failed.push(...result.failed);
      }

      for (const queued of largeFiles) {
        try {
          const result = await uploadFileInChunks(queued.file, destination, (fraction) => {
            setQueuedFiles((prev) =>
              prev.map((qf) =>
                qf.id === queued.id ? { ...qf, progress: Math.round(fraction * 100) } : qf
              )
            );
          });
          uploaded.push(...result.uploaded);
          failed.push(...result.failed);
        } catch (err) {
          failed.push({
            filename: queued.file.name,
            error: err instanceof Error ? err.message : 'Upload failed',
          });
        }
      }

      const result: WorkspaceAssetUploadResult = {
        uploaded,
        failed,
        summary: {
          total: pendingFiles.length,
          successful: uploaded.length,
          failed: failed.length,
        },
      };

      // Update file statuses based on result
      setQueuedFiles((prev) =>
        prev.map((qf) => {
          if (qf.status !== 'uploading') return qf;

          // Check if this file was in the failed list
          const fileFailed = result.failed.find(
            (f) => f.filename === qf.file.name
          );
          if (fileFailed) {
            return { ...qf, status: 'error' as const, error: fileFailed.error };
          }

          // Check if this file was in the uploaded list
          const fileUploaded = result.uploaded.find(
            (f) => f.name === qf.file.name || f.name.startsWith(qf.file.name.split('.')[0])
          );
          if (fileUploaded) {
            return { ...qf, status: 'success' as const };
          }

          // If not found in either, mark as success (could be renamed)
          return { ...qf, status: 'success' as const };
        })
      );

      setUploadResult(result);

      // Refresh the file tree to show newly uploaded files
      if (result.summary.successful > 0) {
        await refreshTree();
        await onUploaded?.();
      }
    } catch (err) {
      // Mark all uploading files as error
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setQueuedFiles((prev) =>
        prev.map((f) =>
          f.status === 'uploading'
            ? { ...f, status: 'error' as const, error: errorMessage }
            : f
        )
      );
    } finally {
      setIsUploading(false);
    }
  }, [queuedFiles, destination, refreshTree, onUploaded]);

  // ----------------------------------------
  // Render
  // ----------------------------------------

  if (!isOpen) return null;

  const pendingCount = queuedFiles.filter((f) => f.status === 'pending').length;
  const successCount = queuedFiles.filter((f) => f.status === 'success').length;
  const errorCount = queuedFiles.filter((f) => f.status === 'error').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-900 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200 dark:border-surface-800">
          <div className="flex items-center gap-2">
            <FolderUp className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-surface-900 dark:text-surface-100">
              Upload Files
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Drop zone */}
          <div
            ref={dropZoneRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={cn(
              'relative border-2 border-dashed rounded-lg p-8 text-center transition-colors',
              isDragging
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-surface-300 dark:border-surface-700 hover:border-primary-400 dark:hover:border-primary-600'
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInputChange}
              className="hidden"
              aria-label="File upload input"
            />

            <Upload className={cn(
              'h-10 w-10 mx-auto mb-4',
              isDragging
                ? 'text-primary-500'
                : 'text-surface-400 dark:text-surface-500'
            )} />

            <p className="text-surface-900 dark:text-surface-100 font-medium mb-1">
              {isDragging ? 'Drop files here' : 'Drag and drop files here'}
            </p>
            <p className="text-sm text-surface-500 dark:text-surface-500 mb-4">
              or
            </p>
            <Button variant="outline" onClick={handleBrowseClick}>
              Browse Files
            </Button>
          </div>

          {/* Queued files list */}
          {queuedFiles.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-surface-900 dark:text-surface-100">
                  Files ({queuedFiles.length})
                </h3>
                {!isUploading && pendingCount > 0 && (
                  <button
                    onClick={clearQueue}
                    className="text-xs text-surface-500 hover:text-surface-700 dark:hover:text-surface-300"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {queuedFiles.map((qf) => {
                  const FileIcon = getFileIcon(qf.file);
                  return (
                    <div
                      key={qf.id}
                      className={cn(
                        'flex items-center gap-3 p-2 rounded-md',
                        qf.status === 'success' && 'bg-green-50 dark:bg-green-900/20',
                        qf.status === 'error' && 'bg-destructive-50 dark:bg-destructive-900/20',
                        qf.status === 'pending' && 'bg-surface-100 dark:bg-surface-800',
                        qf.status === 'uploading' && 'bg-primary-50 dark:bg-primary-900/20'
                      )}
                    >
                      {/* File icon */}
                      <FileIcon className={cn(
                        'h-5 w-5 shrink-0',
                        qf.status === 'success' && 'text-green-600 dark:text-green-400',
                        qf.status === 'error' && 'text-destructive-600 dark:text-destructive-400',
                        qf.status === 'pending' && 'text-surface-500',
                        qf.status === 'uploading' && 'text-primary-600 dark:text-primary-400'
                      )} />

                      {/* File info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-surface-900 dark:text-surface-100 truncate">
                          {qf.file.name}
                        </p>
                        <p className="text-xs text-surface-500 dark:text-surface-500">
                          {qf.status === 'error' && qf.error
                            ? qf.error
                            : formatFileSize(qf.file.size)}
                        </p>
                      </div>

                      {/* Status indicator */}
                      <div className="shrink-0">
                        {qf.status === 'pending' && !isUploading && (
                          <button
                            onClick={() => removeFromQueue(qf.id)}
                            className="p-1 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300"
                            aria-label="Remove file"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                        {qf.status === 'uploading' && (
                          <div className="flex items-center gap-1.5">
                            {qf.progress !== undefined && (
                              <span className="text-xs tabular-nums text-primary-600 dark:text-primary-400">
                                {qf.progress}%
                              </span>
                            )}
                            <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                          </div>
                        )}
                        {qf.status === 'success' && (
                          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                        )}
                        {qf.status === 'error' && (
                          <AlertCircle className="h-4 w-4 text-destructive-600 dark:text-destructive-400" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Upload result summary */}
          {uploadResult && (
            <div className={cn(
              'mt-4 p-3 rounded-md text-sm',
              uploadResult.summary.failed > 0
                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
                : 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
            )}>
              {uploadResult.summary.failed > 0 ? (
                <p>
                  {uploadResult.summary.successful} of {uploadResult.summary.total} files uploaded successfully.
                  {' '}{uploadResult.summary.failed} file(s) failed.
                </p>
              ) : (
                <p>
                  {uploadResult.summary.successful} file(s) uploaded successfully.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-surface-200 dark:border-surface-800">
          <Button variant="outline" onClick={onClose}>
            {successCount > 0 || errorCount > 0 ? 'Close' : 'Cancel'}
          </Button>
          {pendingCount > 0 && (
            <Button
              onClick={handleUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload {pendingCount} file{pendingCount !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FileUpload;
